// Page "Ventes / Devis" : liste et CRUD des devis.

import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { quotationsApi } from "../api/quotations";
import type { QuotationCreate, QuotationLineCreate, QuotationPublic, QuotationStatus, QuotationUpdate } from "../api/quotations";
import { CurrencySelect } from "../components/CurrencySelect";
import { useAuthStore } from "../stores/auth";
import { formatAmount } from "../utils/currency";
import { clientsApi } from "../api/clients";
import { productsApi } from "../api/products";
import type { Product } from "../api/products";
import { intervenantsApi } from "../api/intervenants";
import { projectsApi } from "../api/projects";
import type { ProjectPublic } from "../api/types";
import type { ClientPublic } from "../api/types";
import type { IntervenantPublic } from "../api/types";
import { api, ApiError } from "../api/client";
import { AppShell } from "../components/AppShell";
import { FormField } from "../components/FormField";
import { Modal } from "../components/Modal";

// ---- Helpers ---------------------------------------------------------------

function fmt(n: number | null | undefined, currency = "EUR") {
  if (n == null) return "—";
  return formatAmount(n, currency);
}

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR");
}

function emptyLine(position: number): QuotationLineCreate {
  return { description: "", quantity: 1, unit_price: 0, vat_rate: 20, unit: null, position };
}

function calcHT(line: QuotationLineCreate) {
  return line.quantity * line.unit_price;
}

function calcTotals(lines: QuotationLineCreate[], discountType?: string | null, discountValue?: number | null) {
  const grossHt = lines.reduce((s, l) => s + calcHT(l), 0);
  const grossTtc = lines.reduce((s, l) => s + calcHT(l) * (1 + l.vat_rate / 100), 0);

  let discountHt = 0;
  if (discountType && discountValue && discountValue > 0) {
    if (discountType === "percent") {
      discountHt = parseFloat((grossHt * Math.min(discountValue, 100) / 100).toFixed(2));
    } else {
      discountHt = parseFloat(Math.min(discountValue, grossHt).toFixed(2));
    }
  }
  const ratio = grossHt > 0 ? (grossHt - discountHt) / grossHt : 1;
  const netHt = parseFloat((grossHt - discountHt).toFixed(2));
  const netTtc = parseFloat((grossTtc * ratio).toFixed(2));
  const vat = parseFloat((netTtc - netHt).toFixed(2));
  return { grossHt, discountHt, ht: netHt, vat, ttc: netTtc };
}

// ---- Status badge ----------------------------------------------------------

const STATUS_STYLES: Record<QuotationStatus, string> = {
  draft: "bg-slate-100 text-slate-600",
  sent: "bg-blue-100 text-blue-700",
  accepted: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
  cancelled: "bg-slate-200 text-slate-500",
  invoiced: "bg-violet-100 text-violet-700",
};

function StatusBadge({ status }: { status: QuotationStatus }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labelKey = `salesPage.status${status.charAt(0).toUpperCase()}${status.slice(1)}` as const;
  const label = t(labelKey as Parameters<typeof t>[0]);
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}>
      {label}
    </span>
  );
}

// ---- Dialog state ----------------------------------------------------------

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; quotation: QuotationPublic };

// ---- Main page -------------------------------------------------------------

export function SalesPage() {
  const { t } = useTranslation();
  const [quotations, setQuotations] = useState<QuotationPublic[] | null>(null);
  const [filterStatuses, setFilterStatuses] = useState<Set<QuotationStatus>>(new Set());
  const [listError, setListError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  // Modale de facturation
  type BillingMode = "full" | "percent" | "qty";
  const [billingModal, setBillingModal] = useState<{ open: boolean; quotation: QuotationPublic | null }>({ open: false, quotation: null });
  const [billingMode, setBillingMode] = useState<BillingMode>("full");
  const [billingPercent, setBillingPercent] = useState("100");
  const [billingQtys, setBillingQtys] = useState<Record<string, string>>({});
  const [billingBusy, setBillingBusy] = useState(false);
  const [clients, setClients] = useState<ClientPublic[]>([]);
  const [intervenants, setIntervenants] = useState<IntervenantPublic[]>([]);
  const [projects, setProjects] = useState<ProjectPublic[]>([]);

  async function reload() {
    setListError(null);
    try {
      const [data, cls, ivs] = await Promise.all([
        quotationsApi.list({}),
        clientsApi.list(),
        intervenantsApi.list(),
      ]);
      setQuotations(data);
      setClients(cls);
      setIntervenants(ivs);
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Erreur");
      setQuotations([]);
    }
  }

  useEffect(() => { void reload(); }, []); // eslint-disable-line

  async function onSend(q: QuotationPublic) {
    if (!window.confirm(t("salesPage.sendConfirm"))) return;
    try {
      await quotationsApi.send(q.id);
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur");
    }
  }

  function openBillingModal(q: QuotationPublic) {
    setBillingMode("full");
    setBillingPercent("100");
    // Init qtys depuis les lignes du devis
    const init: Record<string, string> = {};
    (q.lines ?? []).forEach(l => { init[l.id] = String(l.quantity); });
    setBillingQtys(init);
    setBillingModal({ open: true, quotation: q });
  }

  async function submitBilling() {
    const q = billingModal.quotation;
    if (!q) return;
    setBillingBusy(true);
    try {
      let amount_ht: number | undefined;
      let vat_amount: number | undefined;
      let amount_ttc: number | undefined;
      const baseHt = parseFloat(String(q.amount_ht)) || 0;
      const baseTva = parseFloat(String(q.vat_amount)) || 0;
      const baseTtc = parseFloat(String(q.amount_ttc)) || 0;
      if (billingMode === "full") {
        // totalité → pas de payload montant (backend prend les valeurs du devis)
      } else if (billingMode === "percent") {
        const pct = Math.min(100, Math.max(0, parseFloat(billingPercent) || 0)) / 100;
        amount_ht  = parseFloat((baseHt  * pct).toFixed(2));
        vat_amount = parseFloat((baseTva * pct).toFixed(2));
        amount_ttc = parseFloat((baseTtc * pct).toFixed(2));
      } else if (billingMode === "qty") {
        let ht = 0; let ttc = 0;
        (q.lines ?? []).forEach(l => {
          const qty = parseFloat(billingQtys[l.id] ?? String(l.quantity)) || 0;
          const lht = qty * parseFloat(String(l.unit_price));
          const lttc = lht * (1 + parseFloat(String(l.vat_rate)) / 100);
          ht += lht; ttc += lttc;
        });
        amount_ht  = parseFloat(ht.toFixed(2));
        vat_amount = parseFloat((ttc - ht).toFixed(2));
        amount_ttc = parseFloat(ttc.toFixed(2));
      }
      const payload: Record<string, unknown> = {};
      if (amount_ttc !== undefined) { payload.amount_ht = amount_ht; payload.vat_amount = vat_amount; payload.amount_ttc = amount_ttc; }
      await quotationsApi.toInvoice(q.id, payload);
      setBillingModal({ open: false, quotation: null });
      await reload();
      navigate({ pathname: "/invoices", search: "?status=draft" });
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur");
    } finally {
      setBillingBusy(false);
    }
  }

  async function onChangeStatus(q: QuotationPublic, newStatus: QuotationStatus) {
    if (newStatus === q.status) return;
    if (newStatus === "cancelled" && !window.confirm(t("salesPage.cancelConfirm") || "Annuler ce devis ?")) return;
    try {
      await quotationsApi.update(q.id, { status: newStatus });
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur");
    }
  }

  async function onAccept(q: QuotationPublic) {
    try {
      await quotationsApi.update(q.id, { status: "accepted" });
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur");
    }
  }

  async function onDecline(q: QuotationPublic) {
    if (!window.confirm(t("salesPage.declineConfirm"))) return;
    try {
      await quotationsApi.update(q.id, { status: "declined" });
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur");
    }
  }

  async function onDelete(q: QuotationPublic) {
    if (!window.confirm(t("salesPage.deleteConfirm"))) return;
    try {
      await quotationsApi.delete(q.id);
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur");
    }
  }


  async function onDuplicate(q: QuotationPublic) {
    try {
      await quotationsApi.duplicate(q.id);
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur duplication");
    }
  }

  async function downloadPdf(q: QuotationPublic, lang: "fr" | "en" = "fr") {
    try {
      const { blob, filename } = await api.getBlob(`/api/quotations/${q.id}/pdf?lang=${lang}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? `devis-${q.reference ?? q.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur téléchargement PDF");
    }
  }

  const STATUSES: QuotationStatus[] = ["draft", "sent", "accepted", "declined", "cancelled", "invoiced"];

  return (
    <AppShell>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("salesPage.title")}</h1>
          <p className="text-sm text-slate-500">{t("salesPage.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setDialog({ mode: "create" })}
        >
          + {t("salesPage.newQuote")}
        </button>
      </header>

      {/* Filtre statut — pills multi-sélection */}
      <div className="mb-4 flex gap-2 flex-wrap items-center">
        <span className="text-xs text-slate-400 font-medium mr-1">Filtrer :</span>
        <button
          type="button"
          onClick={() => setFilterStatuses(new Set())}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            filterStatuses.size === 0
              ? "bg-slate-800 text-white border-slate-800"
              : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
          }`}
        >
          Tous
        </button>
        {STATUSES.map((s) => {
          const active = filterStatuses.has(s);
          const colorOn: Record<QuotationStatus, string> = {
            draft: "bg-slate-600 text-white border-slate-600",
            sent: "bg-blue-600 text-white border-blue-600",
            accepted: "bg-green-600 text-white border-green-600",
            declined: "bg-red-500 text-white border-red-500",
            cancelled: "bg-slate-400 text-white border-slate-400",
            invoiced: "bg-violet-600 text-white border-violet-600",
          };
          const colorOff: Record<QuotationStatus, string> = {
            draft: "bg-white text-slate-500 border-slate-200 hover:border-slate-400",
            sent: "bg-white text-blue-600 border-blue-200 hover:border-blue-400",
            accepted: "bg-white text-green-600 border-green-200 hover:border-green-400",
            declined: "bg-white text-red-500 border-red-200 hover:border-red-400",
            cancelled: "bg-white text-slate-400 border-slate-200 hover:border-slate-300",
            invoiced: "bg-white text-violet-600 border-violet-200 hover:border-violet-400",
          };
          return (
            <button
              key={s}
              type="button"
              onClick={() => {
                setFilterStatuses(prev => {
                  const next = new Set(prev);
                  if (next.has(s)) next.delete(s); else next.add(s);
                  return next;
                });
              }}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? colorOn[s] : colorOff[s]}`}
            >
              {t(`salesPage.status${s.charAt(0).toUpperCase()}${s.slice(1)}` as Parameters<typeof t>[0])}
            </button>
          );
        })}
      </div>

      {/* Liste */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {listError && <p role="alert" className="p-4 text-sm text-red-600">{listError}</p>}
        {quotations === null && <p className="p-6 text-sm text-slate-500">{t("common.loading")}</p>}
        {quotations !== null && quotations.length === 0 && !listError && (
          <p className="p-6 text-sm text-slate-500">{t("salesPage.noQuotes")}</p>
        )}
        {quotations && (() => {
          const displayed = filterStatuses.size > 0
            ? quotations.filter(q => filterStatuses.has(q.status as QuotationStatus))
            : quotations;
          return displayed.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("salesPage.colRef")}</th>
                <th className="px-4 py-2 font-medium">{t("salesPage.colClient")}</th>
                <th className="px-4 py-2 font-medium">{t("salesPage.colIntervenant")}</th>
                <th className="px-4 py-2 font-medium max-w-xs">{t("salesPage.notes")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("salesPage.colAmount")}</th>
                <th className="px-4 py-2 font-medium">{t("salesPage.colExpiry")}</th>
                <th className="px-4 py-2 font-medium">{t("salesPage.colStatus")}</th>
                <th className="px-4 py-2 font-medium w-48 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((q) => (
                <tr key={q.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">{q.reference ?? "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{q.client_name ?? "—"}</td>
                  <td className="px-4 py-2 text-slate-600">{q.intervenant_name ?? "—"}</td>
                  <td className="px-4 py-2 text-slate-500 max-w-xs"><span className="block truncate" title={q.notes ?? ""}>{q.notes ? q.notes : <span className="text-slate-300">—</span>}</span></td>
                  <td className="px-4 py-2 text-right text-slate-800 font-medium">
                    {fmt(q.amount_ttc, q.currency)}
                    {Number(q.amount_invoiced) > 0 && Number(q.amount_remaining ?? q.amount_ttc) > 0 && (
                      <span className="block text-xs text-amber-600">Reste : {fmt(q.amount_remaining, q.currency)}</span>
                    )}
                    {Number(q.amount_invoiced) > 0 && Number(q.amount_remaining ?? 1) <= 0 && (
                      <span className="block text-xs text-emerald-600">✓ Soldé</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{fmtDate(q.expiry_date)}</td>
                  <td className="px-4 py-2">
                    <select
                      value={q.status}
                      onChange={e => void onChangeStatus(q, e.target.value as QuotationStatus)}
                      className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer focus:ring-1 focus:ring-brand-400 ${
                        q.status === "draft" ? "bg-slate-100 text-slate-600" :
                        q.status === "sent" ? "bg-blue-100 text-blue-700" :
                        q.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                        q.status === "invoiced" ? "bg-violet-100 text-violet-700" :
                        q.status === "cancelled" ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      <option value="draft">Brouillon</option>
                      <option value="sent">Envoyé</option>
                      <option value="accepted">Accepté</option>
                      <option value="invoiced" disabled>Facturé</option>
                      <option value="cancelled">Annulé</option>
                    </select>
                  </td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button
                      type="button"
                      className="text-brand-600 hover:underline"
                      onClick={() => setDialog({ mode: "edit", quotation: q })}
                    >
                      {t("common.edit")}
                    </button>
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-800"
                      title={t("salesPage.downloadPdf")}
                      onClick={() => downloadPdf(q, "fr")}
                    >
                      PDF FR
                    </button>
                    <button
                      type="button"
                      className="btn-secondary text-xs px-2 py-1"
                      title={t("salesPage.downloadPdfEn")}
                      onClick={() => downloadPdf(q, "en")}
                    >
                      PDF EN
                    </button>
                    <button
                      type="button"
                      className="text-amber-600 hover:underline"
                      title={t("salesPage.duplicate")}
                      onClick={() => onDuplicate(q)}
                    >
                      {t("salesPage.duplicate")}
                    </button>
                    {q.status !== "invoiced" && q.status !== "sent" && q.status !== "accepted" && (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => onSend(q)}
                      >
                        {t("salesPage.send")}
                      </button>
                    )}
                    {(q.status === "draft" || q.status === "sent") && (
                      <button
                        type="button"
                        className="text-green-600 hover:underline"
                        onClick={() => onAccept(q)}
                      >
                        {t("salesPage.accept")}
                      </button>
                    )}
                    {q.status === "accepted" && (
                      <button
                        type="button"
                        className="text-slate-500 hover:underline text-xs"
                        onClick={() => onDecline(q)}
                      >
                        {t("salesPage.decline")}
                      </button>
                    )}
                    {q.status !== "cancelled" && (
                      <button
                        type="button"
                        className="text-violet-600 hover:underline"
                        onClick={() => openBillingModal(q)}
                      >
                        {t("salesPage.toInvoice")}
                      </button>
                    )}
                    {q.status !== "invoiced" && (
                      <button
                        type="button"
                        className="text-red-600 hover:underline"
                        onClick={() => onDelete(q)}
                      >
                        {t("common.delete")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          );
        })()}
      </div>

      {/* Modale de facturation depuis devis */}
      {billingModal.open && billingModal.quotation && (
        <Modal
          open={true}
          title={`Facturer ${billingModal.quotation.reference}`}
          onClose={() => { if (!billingBusy) setBillingModal({ open: false, quotation: null }); }}
        >
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
              <p><span className="font-medium">Client :</span> {billingModal.quotation.client_name ?? "—"}</p>
              <p><span className="font-medium">Total devis TTC :</span> {fmt(billingModal.quotation.amount_ttc, billingModal.quotation.currency)}</p>
              {Number(billingModal.quotation.amount_invoiced) > 0 && (
                <p className="text-amber-600 mt-1"><span className="font-medium">Déjà facturé :</span> {fmt(billingModal.quotation.amount_invoiced, billingModal.quotation.currency)} · Reste : {fmt(billingModal.quotation.amount_remaining, billingModal.quotation.currency)}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">Mode de facturation</label>
              <div className="flex flex-col gap-2">
                {(["full","percent","qty"] as const).map(m => (
                  <label key={m} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="billingMode" value={m} checked={billingMode===m} onChange={() => setBillingMode(m)} className="accent-brand-600" />
                    <span className="text-sm">{m==="full" ? "Totalité du devis" : m==="percent" ? "Pourcentage du devis" : "Par quantité (lignes du devis)"}</span>
                  </label>
                ))}
              </div>
            </div>
            {billingMode === "percent" && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Pourcentage (%)</label>
                <input type="number" min="1" max="100" step="1" value={billingPercent} onChange={e => setBillingPercent(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
                <p className="text-xs text-slate-500 mt-1">→ TTC : {((parseFloat(String(billingModal.quotation.amount_ttc))||0) * (parseFloat(billingPercent)||0) / 100).toFixed(2)} {billingModal.quotation.currency}</p>
              </div>
            )}
            {billingMode === "qty" && (billingModal.quotation.lines ?? []).length > 0 && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">Quantités à facturer</label>
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {(billingModal.quotation.lines ?? []).map(l => (
                    <div key={l.id} className="flex items-center gap-2 text-sm border-b border-slate-100 pb-1">
                      <span className="flex-1 text-slate-700 truncate" title={l.description}>{l.description}</span>
                      <span className="text-xs text-slate-400 shrink-0">{String(l.unit_price)}€/{l.unit ?? "u"} TVA {String(l.vat_rate)}%</span>
                      <input type="number" min="0" max={String(l.quantity)} step="0.001"
                        value={billingQtys[l.id] ?? String(l.quantity)}
                        onChange={e => setBillingQtys(p => ({ ...p, [l.id]: e.target.value }))}
                        className="w-20 rounded border border-slate-200 px-2 py-0.5 text-right text-sm shrink-0"
                      />
                      <span className="text-xs text-slate-400 shrink-0">/{String(l.quantity)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs font-medium text-slate-700 mt-2">
                  Total TTC : {(billingModal.quotation.lines ?? []).reduce((acc, l) => {
                    const qty = parseFloat(billingQtys[l.id] ?? String(l.quantity)) || 0;
                    return acc + qty * parseFloat(String(l.unit_price)) * (1 + parseFloat(String(l.vat_rate)) / 100);
                  }, 0).toFixed(2)} {billingModal.quotation.currency}
                </p>
              </div>
            )}
            {billingMode === "qty" && (billingModal.quotation.lines ?? []).length === 0 && (
              <p className="text-xs text-amber-600">Aucune ligne sur ce devis — utilisez le mode Totalité ou Pourcentage.</p>
            )}
            <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
              <button type="button" className="btn-secondary" onClick={() => setBillingModal({ open: false, quotation: null })} disabled={billingBusy}>Annuler</button>
              <button type="button" className="btn-primary" onClick={() => void submitBilling()} disabled={billingBusy}>
                {billingBusy ? "Création en cours…" : "✓ Créer la facture"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <QuotationDialog
        state={dialog}
        clients={clients}
        intervenants={intervenants}
        projects={projects}
        onClose={() => setDialog({ mode: "closed" })}
        onSaved={async () => { setDialog({ mode: "closed" }); await reload(); }}
      />
    </AppShell>
  );
}

// ---- Modale création / édition --------------------------------------------

function QuotationDialog({
  state,
  clients,
  intervenants,
  projects,
  onClose,
  onSaved,
}: {
  state: DialogState;
  clients: ClientPublic[];
  intervenants: IntervenantPublic[];
  projects: ProjectPublic[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isOpen = state.mode !== "closed";

  const tenantCurrency = useAuthStore.getState().currentTenant?.currency ?? "EUR";
  const emptyCreate = (): QuotationCreate => ({
    client_id: null,
    intervenant_id: null,
    project_id: null,
    issue_date: null,
    expiry_date: null,
    currency: tenantCurrency,
    notes: null,
    payment_terms: null,
    discount_type: null,
    discount_value: null,
    lines: [emptyLine(0)],
  });

  const [values, setValues] = useState<QuotationCreate>(emptyCreate());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("");

  useEffect(() => {
    productsApi.list({ active_only: true }).then(r => setProducts(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    if (state.mode === "create") {
      setValues(emptyCreate());
    } else if (state.mode === "edit") {
      const q = state.quotation;
      setValues({
        client_id: q.client_id,
        intervenant_id: q.intervenant_id,
        project_id: q.project_id,
        issue_date: q.issue_date,
        expiry_date: q.expiry_date,
        currency: q.currency,
        notes: q.notes,
        payment_terms: q.payment_terms,
        lines: q.lines.length > 0
          ? q.lines
              .slice()
              .sort((a, b) => a.position - b.position)
              .map((l) => ({
                description: l.description,
                quantity: l.quantity,
                unit_price: l.unit_price,
                vat_rate: l.vat_rate,
                unit: l.unit,
                position: l.position,
              }))
          : [emptyLine(0)],
        discount_type: q.discount_type ?? null,
        discount_value: q.discount_value ?? null,
      });
    }
    setError(null);
  }, [state]); // eslint-disable-line

  if (!isOpen) return null;

  const totals = calcTotals(values.lines, values.discount_type, values.discount_value);

  function setLine(idx: number, patch: Partial<QuotationLineCreate>) {
    setValues((prev) => {
      const lines = prev.lines.map((l, i) => i === idx ? { ...l, ...patch } : l);
      return { ...prev, lines };
    });
  }

  function addLine() {
    setValues((prev) => ({
      ...prev,
      lines: [...prev.lines, emptyLine(prev.lines.length)],
    }));
  }

  function removeLine(idx: number) {
    setValues((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== idx).map((l, i) => ({ ...l, position: i })),
    }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const payload: QuotationCreate = {
        ...values,
        client_id: values.client_id || null,
        intervenant_id: values.intervenant_id || null,
        issue_date: values.issue_date || null,
        expiry_date: values.expiry_date || null,
        notes: values.notes || null,
        payment_terms: values.payment_terms || null,
        lines: values.lines.map((l, i) => ({ ...l, position: i })),
      };

      if (state.mode === "edit") {
        const update: QuotationUpdate = payload;
        await quotationsApi.update(state.quotation.id, update);
      } else {
        await quotationsApi.create(payload);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Impossible d'enregistrer");
    } finally {
      setPending(false);
    }
  }

  const title = state.mode === "edit" ? t("salesPage.editQuote") : t("salesPage.createQuote");

  return (
    <Modal open={isOpen} onClose={onClose} title={title} size="xl">
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Sélecteur produit rapide */}
        {products.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              {t("productsPage.selectProduct")}
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => {
                const pid = e.target.value;
                if (!pid) return;
                const p = products.find(x => x.id === pid);
                if (p) {
                  const newLine = {
                    description: p.name,
                    quantity: 1,
                    unit_price: parseFloat(p.sale_price_ht) || 0,
                    vat_rate: parseFloat(p.vat_rate) || 0,
                    unit: p.unit ?? null,
                    position: values.lines.length,
                  };
                  setValues(prev => ({ ...prev, lines: [...prev.lines, newLine] }));
                }
                setSelectedProduct("");
              }}
              className="input text-sm"
            >
              <option value="">{t("productsPage.selectProduct")}</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.reference ? ` (${p.reference})` : ""} — {p.sale_price_ht} HT
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Client / Intervenant */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t("salesPage.client")} htmlFor="client_id">
            <select
              id="client_id"
              value={values.client_id ?? ""}
              onChange={(e) => setValues({ ...values, client_id: e.target.value || null })}
              className="input"
            >
              <option value="">{t("salesPage.selectClient")}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label={t("salesPage.intervenant")} htmlFor="intervenant_id">
            <select
              id="intervenant_id"
              value={values.intervenant_id ?? ""}
              onChange={(e) => setValues({ ...values, intervenant_id: e.target.value || null })}
              className="input"
            >
              <option value="">{t("salesPage.selectIntervenant")}</option>
              {intervenants.map((iv) => (
                <option key={iv.id} value={iv.id}>{iv.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label={t("salesPage.project")} htmlFor="project_id">
            <select
              id="project_id"
              value={values.project_id ?? ""}
              onChange={(e) => setValues({ ...values, project_id: e.target.value || null })}
              className="input"
            >
              <option value="">{t("salesPage.selectProject")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </FormField>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t("salesPage.issueDate")} htmlFor="issue_date">
            <input
              id="issue_date"
              type="date"
              value={values.issue_date ?? ""}
              onChange={(e) => {
                const issue_date = e.target.value || null;
                let expiry_date = values.expiry_date;
                if (issue_date && values.payment_terms) {
                  const days = parseInt(values.payment_terms.replace(/[^0-9]/g, ""), 10);
                  if (!isNaN(days)) {
                    const d = new Date(issue_date + "T00:00:00");
                    d.setDate(d.getDate() + days);
                    expiry_date = d.toISOString().slice(0, 10);
                  }
                }
                setValues({ ...values, issue_date, expiry_date });
              }}
              className="input"
            />
          </FormField>
          <FormField label={t("salesPage.expiryDate")} htmlFor="expiry_date">
            <input
              id="expiry_date"
              type="date"
              value={values.expiry_date ?? ""}
              onChange={(e) => setValues({ ...values, expiry_date: e.target.value || null })}
              className="input"
            />
          </FormField>
        </div>

        {/* Payment terms / Notes */}
        <FormField label={t("salesPage.paymentTerms")} htmlFor="payment_terms">
          <select
            id="payment_terms"
            value={values.payment_terms ?? ""}
            onChange={(e) => {
              const pt = e.target.value || null;
              let expiry_date = values.expiry_date;
              if (pt && values.issue_date) {
                const days = parseInt(pt.replace(/[^0-9]/g, ""), 10);
                if (!isNaN(days)) {
                  const d = new Date(values.issue_date + "T00:00:00");
                  d.setDate(d.getDate() + days);
                  expiry_date = d.toISOString().slice(0, 10);
                }
              }
              setValues({ ...values, payment_terms: pt, expiry_date });
            }}
            className="input"
          >
            <option value="">{t("salesPage.ptSelect")}</option>
            <option value="0 jours">{t("salesPage.pt0")}</option>
            <option value="30 jours">{t("salesPage.pt30")}</option>
            <option value="45 jours">{t("salesPage.pt45")}</option>
            <option value="60 jours">{t("salesPage.pt60")}</option>
            <option value="90 jours">{t("salesPage.pt90")}</option>
          </select>
        </FormField>
        <FormField label={t("salesPage.notes")} htmlFor="notes">
          <textarea
            id="notes"
            rows={2}
            value={values.notes ?? ""}
            onChange={(e) => setValues({ ...values, notes: e.target.value || null })}
            className="input"
          />
        </FormField>

        {/* Discount */}
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">{t("salesPage.discountTitle")}</p>
          <div className="grid grid-cols-2 gap-4">
            <FormField label={t("salesPage.discountType")} htmlFor="discount_type">
              <select
                id="discount_type"
                value={values.discount_type ?? ""}
                onChange={(e) => setValues({ ...values, discount_type: e.target.value || null, discount_value: null })}
                className="input"
              >
                <option value="">{t("salesPage.discountNone")}</option>
                <option value="percent">{t("salesPage.discountPercent")}</option>
                <option value="fixed">{t("salesPage.discountFixed").replace("{currency}", values.currency)}</option>
              </select>
            </FormField>
            {values.discount_type && (
              <FormField
                label={values.discount_type === "percent"
                  ? t("salesPage.discountLabelPercent")
                  : t("salesPage.discountLabelFixed").replace("{currency}", values.currency)
                }
                htmlFor="discount_value"
              >
                <input
                  id="discount_value"
                  type="number"
                  min={0}
                  step="any"
                  value={values.discount_value ?? ""}
                  onChange={(e) => setValues({ ...values, discount_value: parseFloat(e.target.value) || null })}
                  className="input"
                  placeholder={values.discount_type === "percent"
                    ? t("salesPage.discountPlaceholderPercent")
                    : t("salesPage.discountPlaceholderFixed")
                  }
                />
              </FormField>
            )}
          </div>
        </div>

        {/* Devise */}
        <FormField label={t("salesPage.currency")} htmlFor="currency">
          <CurrencySelect
            id="currency"
            value={values.currency}
            onChange={(v) => setValues({ ...values, currency: v })}
          />
        </FormField>

        {/* Lignes */}
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">{t("salesPage.lines")}</p>
          <div className="space-y-2">
            {values.lines.map((line, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_60px_100px_70px_80px_70px_32px] gap-2 items-end">
                <FormField label={idx === 0 ? t("salesPage.lineDescription") : undefined} htmlFor={`desc-${idx}`}>
                  <input
                    id={`desc-${idx}`}
                    value={line.description}
                    onChange={(e) => setLine(idx, { description: e.target.value })}
                    className="input"
                    placeholder={t("salesPage.lineDescription")}
                  />
                </FormField>
                <FormField label={idx === 0 ? t("salesPage.lineQty") : undefined} htmlFor={`qty-${idx}`}>
                  <input
                    id={`qty-${idx}`}
                    type="number"
                    min={0}
                    step="any"
                    value={line.quantity}
                    onChange={(e) => setLine(idx, { quantity: parseFloat(e.target.value) || 0 })}
                    className="input"
                  />
                </FormField>
                <FormField label={idx === 0 ? t("salesPage.lineUnitPrice") : undefined} htmlFor={`up-${idx}`}>
                  <input
                    id={`up-${idx}`}
                    type="number"
                    min={0}
                    step="any"
                    value={line.unit_price}
                    onChange={(e) => setLine(idx, { unit_price: parseFloat(e.target.value) || 0 })}
                    className="input"
                  />
                </FormField>
                <FormField label={idx === 0 ? t("salesPage.lineVat") : undefined} htmlFor={`vat-${idx}`}>
                  <input
                    id={`vat-${idx}`}
                    type="number"
                    min={0}
                    step="any"
                    value={line.vat_rate}
                    onChange={(e) => setLine(idx, { vat_rate: parseFloat(e.target.value) || 0 })}
                    className="input"
                  />
                </FormField>
                <FormField label={idx === 0 ? t("salesPage.lineUnit") : undefined} htmlFor={`unit-${idx}`}>
                  <input
                    id={`unit-${idx}`}
                    value={line.unit ?? ""}
                    onChange={(e) => setLine(idx, { unit: e.target.value || null })}
                    className="input"
                    placeholder="h"
                  />
                </FormField>
                <FormField label={idx === 0 ? t("salesPage.lineAmountHT") : undefined} htmlFor={`ht-${idx}`}>
                  <input
                    id={`ht-${idx}`}
                    readOnly
                    value={calcHT(line).toFixed(2)}
                    className="input bg-slate-50 text-right"
                  />
                </FormField>
                <div className={idx === 0 ? "mt-5" : ""}>
                  {values.lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      className="text-red-400 hover:text-red-600 text-lg leading-none"
                      title="Supprimer"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addLine}
            className="mt-2 text-sm text-brand-600 hover:underline"
          >
            + {t("salesPage.addLine")}
          </button>
        </div>

        {/* Totaux */}
        <div className="border-t border-slate-100 pt-3 space-y-1 text-sm text-right">
          {totals.discountHt > 0 && (
            <div className="flex justify-end gap-8 text-slate-400">
              <span>{t("salesPage.grossSubtotal")}</span>
              <span className="font-medium w-28 text-right">{formatAmount(totals.grossHt, values.currency)}</span>
            </div>
          )}
          {totals.discountHt > 0 && (
            <div className="flex justify-end gap-8 text-red-500">
              <span>
                {t("salesPage.discountLine") + (values.discount_type === "percent" && values.discount_value ? " (" + values.discount_value + "%)" : "")}
              </span>
              <span className="font-medium w-28 text-right">{"−" + formatAmount(totals.discountHt, values.currency)}</span>
            </div>
          )}
          <div className="flex justify-end gap-8">
            <span className="text-slate-500">{t("salesPage.totalHT")}</span>
            <span className="font-medium w-28 text-right">{formatAmount(totals.ht, values.currency)}</span>
          </div>
          <div className="flex justify-end gap-8">
            <span className="text-slate-500">{t("salesPage.totalVAT")}</span>
            <span className="font-medium w-28 text-right">{formatAmount(totals.vat, values.currency)}</span>
          </div>
          <div className="flex justify-end gap-8 text-base font-semibold text-slate-900">
            <span>{t("salesPage.totalTTC")}</span>
            <span className="w-28 text-right">{formatAmount(totals.ttc, values.currency)}</span>
          </div>
        </div>

        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            className="text-slate-500 hover:text-slate-800 text-sm"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={pending} className="btn-primary">
            {pending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
